import { ActionDialog, CustomDialog, Dialog, InfoDialog } from "@atoms/Dialog";
import React, { createContext, useRef, useState } from "react";

export enum DialogMode {
	Info = "Info",
	Custom = "Custom",
	Action = "Action",
	Form = "Form",
}

export enum ContainerType {
	large = "large",
	short = "short",
	tiny = "tiny",
}

interface InfoDialogProps {
	title?: string;
}

interface ActionDialogProps {
	mainButton?: any;
	secondaryButton?: any;
}

interface ComponentProps extends InfoDialogProps, ActionDialogProps {}

interface ModalContext {
	showModal: (component: any, option?: UseDialogOption) => void;
	hideModal: VoidFunction;
	forceHideModal: VoidFunction;
	onFormDialogClose: VoidFunction;
}

const defaultModalContext = {
	showModal: () => Promise.resolve(),
	hideModal: () => Promise.resolve(),
	forceHideModal: () => Promise.resolve(),
	onFormDialogClose: () => Promise.resolve(),
};

interface Props {
	children: React.ReactNode;
}

interface UseDialogOption extends ComponentProps {
	mode?: DialogMode;
	container?: ContainerType;
	forceNoSheetClosed?: boolean;
	onClose?: VoidFunction;
	className?: string;
}

export const ModalContext = createContext<ModalContext>(defaultModalContext);

const MODAL_DEFAULT_OPTION = {
	mode: DialogMode.Info,
	container: ContainerType.short,
	onClose: () => {},
	className: "",
};

export const ModalContextProvider: React.FC<Props> = ({ children }) => {
	const modalRef = useRef<HTMLDivElement>();
	const [content, setContent] = useState<any>(null);
	const [modalOption, setModalOption] =
		useState<UseDialogOption>(MODAL_DEFAULT_OPTION);

	const forceNoSheetClosed = useRef(false);

	const resetState = () => {
		setContent(null);
		setModalOption(MODAL_DEFAULT_OPTION);
	};

	const onFormDialogClose = () => {
		const formContainer = modalRef.current?.querySelector(
			".FormContainer.form-dirty",
		);
		if (formContainer) {
			// TODO: show alert
		} else {
			resetState();
		}
	};

	const forceHideModal = () => {
		resetState();
		modalOption.onClose && modalOption.onClose();
	};

	const hideModal = () => {
		if (!forceNoSheetClosed.current) {
			resetState();
			modalOption.onClose && modalOption.onClose();
		}
	};

	const onBackdropClicked = () => {
		if (modalOption.mode === DialogMode.Form) {
			onFormDialogClose();
			return;
		}

		hideModal();
	};

	const getDialogContentByMode = (
		mode: DialogMode,
		component: any,
		componentProps?: ComponentProps,
	) => {
		switch (mode) {
			case DialogMode.Form:
				return (
					<CustomDialog hideModal={onFormDialogClose} {...componentProps}>
						{component}
					</CustomDialog>
				);
			case DialogMode.Custom:
				return (
					<CustomDialog hideModal={hideModal} {...componentProps}>
						{component}
					</CustomDialog>
				);
			case DialogMode.Action:
				return (
					<ActionDialog hideModal={hideModal} {...componentProps}>
						{component}
					</ActionDialog>
				);
			case DialogMode.Info:
			default:
				return (
					<InfoDialog hideModal={hideModal} {...componentProps}>
						{component}
					</InfoDialog>
				);
		}
	};

	const showModal = (component: any, option?: UseDialogOption) => {
		const mergedOption = { ...MODAL_DEFAULT_OPTION, ...option };
		const { mode, container, onClose, className, ...others } = mergedOption;

		setModalOption({
			container: container as ContainerType,
			onClose,
			mode,
			className,
		});

		forceNoSheetClosed.current = Boolean(mergedOption.forceNoSheetClosed);

		const content = getDialogContentByMode(
			mode,
			component,
			others as ComponentProps,
		);
		setContent(content);
	};

	return (
		<ModalContext.Provider
			value={{ showModal, hideModal, onFormDialogClose, forceHideModal }}
		>
			<>
				{children}
				{Boolean(content) && (
					<Dialog
						onBackdropClicked={onBackdropClicked}
						container={modalOption.container}
						ref={modalRef}
						className={modalOption.className}
					>
						{content}
					</Dialog>
				)}
			</>
		</ModalContext.Provider>
	);
};
